import type { WorkspaceFile } from '../../../lib/workspace-files';
import { useTranslation } from '../../../lib/i18n';
import { useAgentName } from '../../../lib/use-agent-name';
import { extensionOf, humanSize, typeLabel } from '../../../lib/artifact-file';
import { creatorLabel, creatorOf } from '../../../lib/file-browser';
import { formatConversationDate } from '../../../lib/conversations';
import styles from './FileDetailMeta.module.css';

type TFunc = (key: string, options?: Record<string, unknown>) => string;

/** What the attribution line needs beyond the file itself. An object rather than two trailing positional
 *  strings: at a call site `fileSubtitle(item, t, name, other)` says nothing about which is which, and the
 *  assistant name is the one that MUST be threaded (see `fileSubtitle`). */
export interface FileSubtitleContext {
  /** The owning Space's display name — the trailing segment. Only the surfaces that know it pass it. */
  workspaceName?: string;
  /** The live primary-assistant name (`useAgentName().short`), for a writer that named none. */
  assistantName?: string;
}

// The name/kind/attribution subtitle + Kind/Size/Type/Date metadata rows — shared by BOTH the in-app
// viewer's own header (`DocViewerModal`, the mobile-web standalone file page) and the no-viewer fallback
// (`FileDetailBody`), so an artifact reads identically whichever shell hosts it (Figma node 8224-139650).
export function FileDetailMeta({ item, sizeBytes, workspaceName }: { item: WorkspaceFile; sizeBytes: number | null; workspaceName?: string }) {
  const { t } = useTranslation();
  const agent = useAgentName();
  const subtitle = fileSubtitle(item, t, { workspaceName, assistantName: agent.short });

  return (
    <div>
      <h2 className={styles.title}>{item.name}</h2>
      {/* Omitted, not emptied: with neither an agent nor a space to name there is no line to draw, and an
          empty <p> still spends its line-height under the title. */}
      {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
      <FileDetailRows item={item} sizeBytes={sizeBytes} />
    </div>
  );
}

/**
 * The attribution line every file surface shows under a file's name: **`built by {agent} · {space}`**
 * (Figma `15652:57057` reads "built by Melody").
 *
 * ONE function for it, because three surfaces render this exact string — the viewer modal's header
 * (`DocViewerModal`, via `Modal`'s `description`), the mobile-web standalone file page
 * (`MobileFileDetailView`) and the no-viewer fallback (`FileDetailMeta` above) — and the middle one used
 * to re-derive it inline, which is how it kept its own copy of the wording.
 *
 * **NO `File` / `App` kind segment.** It used to lead with one, and the redesign drops it: the kind is
 * already legible from the file name, the icon and the document itself, so the word only pushed the two
 * facts a reader actually wants along the line. `agentCreations.kind.file`/`.app` stay in the catalog —
 * the Kind metadata row and `MobileFileDetailView`'s definition list still label with them.
 *
 * The wording is `agentCreations.detail.builtBy` ("built by {agent}"), which is NATIVE's key and native's
 * lowercase phrasing, so all three surfaces say the same thing in all 37 languages. Deliberately not
 * `files.builtBy` — that is the Files table's COLUMN HEADER ("Built by", capitalised), a different thing in
 * a different place — and no longer `files.createdBy`, which survives as the filter chip's label.
 *
 * Either segment may be missing (`workspaceName` is threaded from only the surfaces that know it), so the
 * line degrades to whichever segments exist and returns **''** when there are none. Every caller must
 * therefore render it conditionally — a bare join would leave a stray " · " on a file with no attribution.
 *
 * ⚠️ **THE NAME IS RESOLVED, NOT READ.** `item.builtBy.name` is EMPTY on most real files — the harness
 * stamps an actor kind (`{ agentId: 'MAESTRO', name: '' }`) and only a sub-agent running AS a named team
 * agent carries a name (measured on a live account: 32 of 43 files, 74%). Reading `.name` straight through
 * rendered **"built by  · Space"** — the empty interpolation plus the separator, i.e. exactly the half-drawn
 * line this function's contract forbids. So the writer goes through `creatorOf` + `creatorLabel`, the SAME
 * ladder the Files table's "Built by" column uses (own name → the live assistant name for an unnamed agent →
 * a localized actor label → the kind humanised), and the segment is dropped only when there is no writer at
 * all. That also makes the two surfaces agree: the header cannot say nothing about a file whose row in the
 * table two clicks earlier named its writer.
 *
 * Native does the trimming half of this in `attributionSubtitle` (iOS `AgentCreationsScreen.swift`, Android
 * `FileDetailScreen.kt`) — an empty/whitespace agent name contributes no segment there either.
 */
export function fileSubtitle(item: WorkspaceFile, t: TFunc, { workspaceName, assistantName }: FileSubtitleContext = {}): string {
  const creator = creatorOf(item);
  const agent = creator ? creatorLabel(creator, t, assistantName).trim() : '';
  const parts = [agent ? t('agentCreations.detail.builtBy', { agent }) : null, workspaceName?.trim() || null].filter(Boolean);
  return parts.join(' · ');
}

// The Kind/Size/Type/Date rows on their own — the file-detail page renders them as a definition list.
//
// There is no one-line `FileMetaLine` flavour of them any more: it existed for the viewer modal, which drew
// it as a caption UNDER the document, and the redesign deletes that row outright (both phones dropped
// theirs too). The facts a reader wants are in the header attribution now.
export function FileDetailRows({ item, sizeBytes }: { item: WorkspaceFile; sizeBytes: number | null }) {
  const { t } = useTranslation();
  const type = !item.isApp ? typeLabel(extensionOf(item)) : null;

  return (
    <dl className={styles.meta}>
      <MetaRow label={t('agentCreations.detail.kind')} value={item.isApp ? t('agentCreations.kind.app') : t('agentCreations.kind.file')} />
      {sizeBytes != null && <MetaRow label={t('agentCreations.detail.size')} value={humanSize(sizeBytes)} />}
      {!item.isApp && type && <MetaRow label={t('agentCreations.detail.type')} value={type} />}
      {item.updatedDate && <MetaRow label={t('agentCreations.table.date')} value={formatConversationDate(item.updatedDate)} />}
    </dl>
  );
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.metaRow}>
      <dt className={styles.metaLabel}>{label}</dt>
      <dd className={styles.metaValue}>{value}</dd>
    </div>
  );
}
